import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Talent Recruitment & Staffing",
  "Temporary & Contractual Hiring",
  "Recruitment Process Outsourcing (RPO)",
  "HR Strategy & Compliance Advisory",
  "Culture Building & Engagement",
  "Custom Software Development",
  "ERP Implementation (Frappe/ERPNext)",
  "Mobile & Web App Development",
  "Business Automation Solutions",
  "IT Consulting & Support",
  "General Enquiry",
];

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: null });
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!/^[+]?[\d\s()-]{7,20}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (!form.service) e.service = "Please select a service.";
    if (form.message.trim().length < 10) e.message = "Please tell us a bit more (min. 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service,
        message: form.message.trim(),
      });
      toast.success("Consultation booked! Our team will reach out within 24 hours.");
      setForm(initialForm);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls = (key) => `form-input-dark w-full rounded-xl px-4 py-3.5 text-sm ${errors[key] ? "input-error" : ""}`;

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-28 relative hero-radial grid-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#F89335]">Get In Touch</p>
          <h2 data-testid="contact-title" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Let's <span className="text-gradient-gold">Connect</span>
          </h2>
          <p data-testid="contact-text" className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-lg">
            Need help finding the right people? Looking to automate and scale your systems? We're here to help.
          </p>

          <div className="mt-9 flex flex-wrap gap-4">
            <a
              href="tel:+919880465477"
              data-testid="contact-phone-button"
              className="btn-primary-glow font-['Poppins'] font-semibold px-7 py-3.5 rounded-xl inline-flex items-center gap-2.5"
            >
              <Phone size={18} /> Call Us
            </a>
            <a
              href="mailto:info@krijotech.com"
              data-testid="contact-email-button"
              className="btn-ghost-glass font-['Poppins'] font-semibold px-7 py-3.5 rounded-xl inline-flex items-center gap-2.5 text-white"
            >
              <Mail size={18} /> Email Us
            </a>
          </div>

          <div className="mt-10 space-y-4 text-sm text-slate-400">
            <p className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-[#F89335]/12 flex items-center justify-center"><Phone size={16} className="text-[#F89335]" /></span>
              <span data-testid="contact-phone-number">+91 98804 65477</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-[#F89335]/12 flex items-center justify-center"><Mail size={16} className="text-[#F89335]" /></span>
              <span data-testid="contact-email-address">info@krijotech.com</span>
            </p>
          </div>
        </motion.div>

        <motion.form
          data-testid="consultation-form"
          onSubmit={onSubmit}
          noValidate
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="glass-card rounded-3xl p-7 sm:p-9"
        >
          <h3 className="font-['Poppins'] font-semibold text-xl sm:text-2xl">Book a Free Consultation</h3>
          <p className="text-sm text-slate-400 mt-1.5">Tell us what you need — we'll get back within 24 hours.</p>

          <div className="mt-7 grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="cf-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Full Name</label>
              <input id="cf-name" data-testid="form-name-input" type="text" placeholder="Your name" value={form.name} onChange={set("name")} className={fieldCls("name")} />
              {errors.name && <p data-testid="form-name-error" className="text-xs text-red-400 mt-1.5">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="cf-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Email</label>
              <input id="cf-email" data-testid="form-email-input" type="email" placeholder="you@company.com" value={form.email} onChange={set("email")} className={fieldCls("email")} />
              {errors.email && <p data-testid="form-email-error" className="text-xs text-red-400 mt-1.5">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="cf-phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Phone</label>
              <input id="cf-phone" data-testid="form-phone-input" type="tel" placeholder="+91 98804 65477" value={form.phone} onChange={set("phone")} className={fieldCls("phone")} />
              {errors.phone && <p data-testid="form-phone-error" className="text-xs text-red-400 mt-1.5">{errors.phone}</p>}
            </div>
            <div>
              <label htmlFor="cf-service" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Service Needed</label>
              <select id="cf-service" data-testid="form-service-select" value={form.service} onChange={set("service")} className={`${fieldCls("service")} appearance-none`}>
                <option value="" disabled className="bg-[#050942]">Select a service</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s} className="bg-[#050942]">{s}</option>
                ))}
              </select>
              {errors.service && <p data-testid="form-service-error" className="text-xs text-red-400 mt-1.5">{errors.service}</p>}
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="cf-message" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Message</label>
              <textarea id="cf-message" data-testid="form-message-input" rows={4} placeholder="Tell us about your requirement..." value={form.message} onChange={set("message")} className={`${fieldCls("message")} resize-none`} />
              {errors.message && <p data-testid="form-message-error" className="text-xs text-red-400 mt-1.5">{errors.message}</p>}
            </div>
          </div>

          <button
            type="submit"
            data-testid="consultation-form-submit"
            disabled={submitting}
            className="btn-primary-glow font-['Poppins'] font-semibold w-full mt-7 px-8 py-4 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {submitting ? "Submitting..." : "Book My Free Consultation"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
